import { useRef, useState } from 'react';

interface NativeVideoPlayerProps {
  /** Progressive-download MP4 (faststart) URL */
  src: string;
  /** Poster shown before playback — prevents a black box */
  poster?: string;
  /** Extra classes for the wrapper div */
  className?: string;
}

/**
 * Native <video> per the video component spec (Jaco 2026-09-17):
 * progressive-download MP4 with faststart served straight off Hosting's /
 * Storage's CDN. preload="metadata" is NON-NEGOTIABLE — a 48MB file must not
 * download until the user presses play; the poster prevents a black box;
 * playsInline keeps iPhones on the page; explicit dimensions stop layout
 * jump. Native controls only — no facade, no custom player, no
 * autoplay/muted/loop.
 *
 * Branded play overlay: a green pulsing play button over the poster to grab
 * attention. It unmounts the moment playback starts (onPlay also catches a
 * play begun via the native controls), handing over to the native controls.
 *
 * Shared by /education (the page's centrepiece) and the home page's
 * wealth-strategy section (same video next to the strategy form).
 */
export default function NativeVideoPlayer({ src, poster, className = '' }: NativeVideoPlayerProps) {
  const videoRef = useRef<HTMLVideoElement>(null);
  const [started, setStarted] = useState(false);

  return (
    <div className={`relative ${className}`}>
      <video
        ref={videoRef}
        onPlay={() => setStarted(true)}
        controls
        // Hides Chrome's 3-dot Download option (deterrent only — the mp4 is
        // a public asset either way).
        controlsList="nodownload"
        preload="metadata"
        poster={poster || undefined}
        width={1280}
        height={720}
        playsInline
        className="w-full h-auto max-w-full rounded-xl shadow-card bg-gray-900"
      >
        <source src={src} type="video/mp4" />
        Your browser does not support video playback.
      </video>
      {!started && (
        <button
          type="button"
          onClick={() => videoRef.current?.play()}
          aria-label="Play the video"
          className="absolute inset-0 flex items-center justify-center rounded-xl group cursor-pointer"
        >
          <span className="relative flex items-center justify-center">
            <span className="absolute h-20 w-20 md:h-24 md:w-24 rounded-full bg-primary/40 animate-ping" />
            <span className="relative w-16 h-16 md:w-20 md:h-20 bg-primary rounded-full flex items-center justify-center shadow-xl group-hover:scale-110 group-hover:bg-primary/90 transition-all">
              <svg className="w-7 h-7 md:w-9 md:h-9 text-white ml-1" fill="currentColor" viewBox="0 0 24 24">
                <path d="M8 5v14l11-7z" />
              </svg>
            </span>
          </span>
        </button>
      )}
    </div>
  );
}
